import { useState } from "react";
import {
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  SurfaceCard,
  Typography,
  notification,
} from "@murm/ant";
import { EllipsisOutlined } from "@ant-design/icons";
import { PENDO_ID, pendoId } from "../../../lib/pendoIds";
import styles from "./SavedSearchesPanel.module.css";

const { Link, Text, Title } = Typography;

// A fixed page size sidesteps measuring real rendered row heights against a container whose
// ancestor height doesn't always resolve correctly.
const PAGE_SIZE = 12;
/**
 * Rename/Delete both open a modal, each followed by a success toast naming the search, bolded.
 */
const SavedSearchesPanel = ({
  savedSearches = [],
  onSelectSearch = () => {},
  onRename = () => {},
  onDelete = () => {},
  // Stacked/narrow layout (LandingPage.js) renders this full-width on the page background, same
  // as the Templates section above -- no card. Only the two-column desktop rail is carded.
  bordered = true,
}) => {
  const [renamingQuery, setRenamingQuery] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingQuery, setDeletingQuery] = useState(null);

  const startRename = (search) => {
    setRenamingQuery(search.query);
    setRenameValue(search.label);
  };

  const commitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed) {
      onRename(renamingQuery, trimmed);
      notification.success({
        message: "Saved Search Renamed",
        description: (
          <>
            <strong>{trimmed}</strong> has been successfully renamed.
          </>
        ),
      });
    }
    setRenamingQuery(null);
  };

  const deletingSearch = savedSearches.find((search) => search.query === deletingQuery);

  const needsPagination = savedSearches.length > PAGE_SIZE;
  const totalPages = Math.ceil(savedSearches.length / PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  // Derived, not synced via an effect: a delete can leave `currentPage` past the new last page --
  // clamped here, at render, instead.
  const safeCurrentPage = totalPages > 0 ? Math.min(currentPage, totalPages) : 1;

  const visibleSearches = needsPagination
    ? savedSearches.slice((safeCurrentPage - 1) * PAGE_SIZE, safeCurrentPage * PAGE_SIZE)
    : savedSearches;

  const renderSearchRow = (search) => (
    <div key={search.id} className={styles.row}>
      {/* Passes the whole search, not just its query string, so a caller with a real `parameters`
          parse result can re-tokenize it into real tokens -- see LandingPage.js. */}
      <Link
        {...pendoId(PENDO_ID.SAVED_SEARCH_LINK)}
        onClick={() => onSelectSearch(search)}
        className={styles.label}
      >
        {search.label}
      </Link>
      <Dropdown
        trigger={["click"]}
        menu={{
          items: [
            {
              key: "rename",
              label: "Rename",
              ...pendoId(PENDO_ID.SAVED_SEARCH_RENAME),
              onClick: () => startRename(search),
            },
            {
              key: "delete",
              label: "Delete",
              ...pendoId(PENDO_ID.SAVED_SEARCH_DELETE),
              danger: true,
              onClick: () => setDeletingQuery(search.query),
            },
          ],
        }}
      >
        {/* Horizontal dots (EllipsisOutlined), not vertical (MoreOutlined), per Figma. */}
        <EllipsisOutlined
          {...pendoId(PENDO_ID.SAVED_SEARCH_MENU)}
          className={styles.menu}
          aria-label="Saved search options"
        />
      </Dropdown>
    </div>
  );

  const body = (
    <div
      data-testid="saved-searches-body"
      className={bordered ? `${styles.body} ${styles.bodyBordered}` : styles.body}
    >
      <Title level={4} className={styles.title}>
        Saved Searches
      </Title>
      {savedSearches.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className={styles.empty}
          description={
            <>
              <div className={styles.emptyTitle}>No Saved Searches</div>
              <div className={styles.emptyHint}>
                Save searches you revisit often to avoid starting from scratch.
              </div>
            </>
          }
        />
      ) : (
        <>
          <div
            data-testid="saved-searches-list"
            className={bordered ? styles.listBordered : undefined}
          >
            {visibleSearches.map(renderSearchRow)}
            {/* Inside the scrolling list, not a fixed footer -- scrolls with the content instead
                of staying pinned at the bottom. */}
            {needsPagination && (
              <div className={styles.pagination}>
                <Pagination
                  current={safeCurrentPage}
                  pageSize={PAGE_SIZE}
                  total={savedSearches.length}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const modals = (
    <>
      <Modal
        open={Boolean(renamingQuery)}
        onCancel={() => setRenamingQuery(null)}
        title="Rename Saved Search"
        footer={[
          <Button
            {...pendoId(PENDO_ID.SAVED_SEARCH_RENAME_CANCEL)}
            key="cancel"
            onClick={() => setRenamingQuery(null)}
          >
            Cancel
          </Button>,
          <Button
            {...pendoId(PENDO_ID.SAVED_SEARCH_RENAME_SAVE)}
            key="save"
            type="primary"
            onClick={commitRename}
          >
            Save
          </Button>,
        ]}
      >
        <Text className={styles.fieldLabel}>Name</Text>
        <Input
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
          }}
          aria-label="Name"
        />
      </Modal>

      <Modal
        open={Boolean(deletingQuery)}
        onCancel={() => setDeletingQuery(null)}
        title="Delete this saved search?"
        footer={[
          <Button
            {...pendoId(PENDO_ID.SAVED_SEARCH_DELETE_CANCEL)}
            key="cancel"
            onClick={() => setDeletingQuery(null)}
          >
            Cancel
          </Button>,
          <Button
            {...pendoId(PENDO_ID.SAVED_SEARCH_DELETE_CONFIRM)}
            key="delete"
            danger
            type="primary"
            onClick={() => {
              onDelete(deletingQuery);
              notification.success({
                message: "Saved Search Deleted",
                description: (
                  <>
                    <strong>{deletingSearch?.label}</strong> has been successfully deleted.
                  </>
                ),
              });
              setDeletingQuery(null);
            }}
          >
            Delete
          </Button>,
        ]}
      >
        <Text>{deletingSearch?.label}</Text>
      </Modal>
    </>
  );

  return bordered ? (
    <SurfaceCard className={styles.card}>
      {body}
      {modals}
    </SurfaceCard>
  ) : (
    // Not a card -- plain white fill reaching the page edges, against the gray colorBgLayout
    // background, per the Figma Tablet Portrait/Narrow frames.
    <div className={styles.plain}>
      {body}
      {modals}
    </div>
  );
};

export default SavedSearchesPanel;
